class CartDrawer extends HTMLElement {
  /*
   * Cart apps refresh the drawer by replacing this whole <cart-drawer> element
   * (Discounty's sdk.js calls replaceWith on it after adding a free gift). Any
   * reference captured earlier then points at a detached node, so always resolve
   * the drawer that is actually in the document before acting on it.
   */
  static current() {
    return document.querySelector('cart-drawer');
  }

  // Open state lives on the class, not the element, so it survives a replacement.
  static isOpen = false;

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // A replacement element arrives with the server-rendered class list, which knows
    // neither that the drawer was open nor whether its own contents are empty.
    this.classList.toggle('active', CartDrawer.isOpen);
    if (CartDrawer.isOpen) this.classList.add('animate');
    this.syncEmptyState();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // This constructor runs again every time the drawer element is replaced. Bind the
    // icon once, and resolve the live drawer at click time so a detached instance
    // can't swallow the click (and lock body scroll for a drawer nobody can see).
    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      CartDrawer.current()?.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        CartDrawer.current()?.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Delegate to the live element if this instance has been swapped out, otherwise
    // `active` lands on a detached node while body scroll is locked for real.
    if (!this.isConnected) {
      const live = CartDrawer.current();
      if (live && live !== this) live.open(triggeredBy);
      return;
    }

    CartDrawer.isOpen = true;

    // Last line of defence: whatever happened while the drawer was closed - an app
    // swapping the element, a cart response landing late - it must never become
    // visible while is-empty disagrees with the rows it holds. That combination
    // blanks the drawer (.is-empty .cart__contents) and sends trapFocus a null
    // container via the missing .drawer__inner-empty.
    this.syncEmptyState();

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // .drawer__inner-empty only exists in the empty-cart markup, so fall back to
        // the drawer itself - trapFocus(null) throws inside getFocusableElements.
        const emptyContainer = this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty');
        const containerToTrapFocusOn = emptyContainer || document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    CartDrawer.isOpen = false;
    this.classList.remove('active');

    const live = CartDrawer.current();
    if (live && live !== this) live.classList.remove('active');

    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /*
   * `is-empty` on <cart-drawer> drives `.is-empty .cart__contents { display: none }`
   * (component-cart.css) and hides the drawer header, so it has to agree with the
   * markup currently inside the drawer. Derive it from the DOM instead of trusting
   * whichever async cart response - or replacement element - arrived last.
   *
   * .drawer__cart-items-wrapper is rendered only inside `{% if cart != empty %}` in
   * snippets/cart-drawer.liquid, so its presence is the authoritative signal.
   */
  isEmpty() {
    return !this.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper');
  }

  syncEmptyState() {
    const empty = this.isEmpty();
    this.classList.toggle('is-empty', empty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    return empty;
  }

  renderContents(parsedState) {
    if (!this.isConnected) {
      const live = CartDrawer.current();
      if (live && live !== this) return live.renderContents(parsedState);
    }

    // This repaint carries the freshest cart state, so supersede any older in-flight one.
    CartRenderQueue.begin();
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncEmptyState();

    setTimeout(() => {
      const drawer = CartDrawer.current() || this;
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      drawer.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
